"use client";

import ServiceTemplatePage from "@components/services/ServiceTemplatePage";
import SectionHead from "@components/services/SectionHead";
import AnswerCard from "@components/services/AnswerCard";
import KeyFactsBar from "@components/services/KeyFactsBar";
import ScopeGrid from "@components/services/ScopeGrid";
import TimelineSteps from "@components/services/TimelineSteps";
import WhyDbiCards from "@components/services/WhyDbiCards";
import PainPointsGrid from "@components/services/PainPointsGrid";
import ProcessSteps from "@components/services/ProcessSteps";
import CtaBlock from "@components/services/CtaBlock";

interface ManagedBIClientProps {
  landingPageData: {
    hero: {
      title: string;
      highlightedText: string;
      description: string;
      backgroundImage: string;
      ctaText: string;
      ctaSecondaryText: string;
    };
    answer?: {
      question?: string;
      paragraphs: string[];
      keyfacts?: { label: string; sublabel: string }[];
    };
    whyManagedAI: {
      titleLine2: string;
      description: string;
      sections: { title: string; description: string }[];
    };
    timeline: {
      stages: { duration: string; title: string; description: string }[];
    };
    pricing: {
      seoChip: string;
      titleLine1: string;
      titleLine2: string;
      description: string;
      billingPeriods: { id: string; label: string; multiplier: number }[];
      tiers: {
        id: string;
        name: string;
        persona: string;
        description: string;
        basePriceMonthly: number;
        features: {
          tokens: string;
          dataPortability: boolean;
          dataGovernanceAutomation: boolean;
          slaResponse: string;
          dedicatedContact: boolean;
          humanExpertHours: number;
          kanbanBoard: boolean;
          pdfReporting: boolean;
          aeHours: number;
          effectiveHours: number;
        };
        highlighted?: boolean;
      }[];
      featureComparison: any[];
      aeExplainer: any;
      overagePolicy: any;
      addOnsHeading: {
        title: string;
        subtitle: string;
      };
      addOns: any[];
      ctaText: string;
      priceLabelAnnual?: string;
      priceFootnote?: string;
      serviceScope?: {
        title?: string;
        description?: string;
        developmentMigration?: string;
        services?: string[];
        supportedTools?: string[];
      };
    };
    faq: {
      titleLine2: string;
      faqs: { question: string; answer: string }[];
    };
    relatedServices: {
      tag: string;
      title: string;
      description: string;
      href: string;
    }[];
    painPoints: {
      title: string;
      items: { id: string; title: string; description: string; icon?: string }[];
    };
    consultingShift: {
      titleLine1: string;
      description: string;
      sections: {
        id: string;
        title: string;
        description: string;
        icon?: string;
        benefits: string[];
      }[];
    };
    technicalSetup: {
      titleLine1: string;
      description: string;
      sections: { id: string; title: string; description: string; icon?: string }[];
    };
    complianceMatters: {
      titleLine1: string;
      description: string;
      sections: {
        id: string;
        title: string;
        description: string;
        icon?: string;
        benefits: string[];
      }[];
    };
    videoChaptersHeader: {
      titleLine1: string;
      description: string;
    };
    livePromptingChapters: {
      id: string;
      chapter: string;
      timeSavings: { hoursSaved: number; minutesTaken: number };
      tasks: string[];
    }[];
    agentDashboard: {
      titleLine1: string;
      titleLine2: string;
      description: string;
      introTitle: string;
      introDescription: string;
      primaryButton: { text: string; href: string };
      secondaryButton: { text: string; href: string };
    };
    cta: {
      titleLine2: string;
      description: string;
      primaryButton: { text: string; href: string };
      secondaryButton: { text: string; href: string };
    };
  };
}

export default function ManagedBIClient({ landingPageData }: ManagedBIClientProps) {
  if (!Array.isArray(landingPageData.pricing.billingPeriods)) {
    throw new Error(
      "ManagedBIClient: missing `pricing.billingPeriods` in managed-agentic-bi.json."
    );
  }

  const hero = {
    badge: "Subscription Service",
    title: landingPageData.hero.title,
    highlight: landingPageData.hero.highlightedText,
    highlightPosition: "after" as const,
    description: landingPageData.hero.description,
    leadMeta: [
      "Managed Service",
      "ABIS Automation",
      "DACH Remote",
      "Microsoft Partner",
    ],
    photoSrc: landingPageData.hero.backgroundImage,
    photoAlt: landingPageData.hero.title,
    tag: "Managed Agentic BI",
    primaryCta: {
      label: landingPageData.hero.ctaText,
      href: "#leistungen",
      variant: "primary" as const,
    },
    secondaryCta: {
      label: landingPageData.hero.ctaSecondaryText,
      href: "/services/power-bi-mcp-server",
      variant: "ghost-inverse" as const,
    },
  };

  const sections = [
    {
      id: "einleitung",
      content: (
        <>
          <SectionHead
            eyebrow="01 · Direkte Antwort"
            title={
              landingPageData.answer?.question ??
              "Was ist Managed Agentic BI?"
            }
            blue
          />
          <AnswerCard
            paragraphs={
              landingPageData.answer?.paragraphs ?? [
                landingPageData.hero.description,
              ]
            }
          />
          <KeyFactsBar facts={landingPageData.answer?.keyfacts} />
        </>
      ),
    },
    {
      id: "leistungen",
      content: (
        <>
          <SectionHead
            eyebrow="02 · Leistungsumfang"
            title={landingPageData.whyManagedAI.titleLine2}
            lede={landingPageData.whyManagedAI.description}
            blue
          />
          <ScopeGrid
            items={landingPageData.whyManagedAI.sections.map((section) => ({
              title: section.title,
              description: section.description,
            }))}
          />
        </>
      ),
    },
    {
      id: "pain-points",
      content: (
        <>
          <SectionHead
            eyebrow="03 · Ausgangslage"
            title={landingPageData.painPoints.title}
            blue
          />
          <PainPointsGrid items={landingPageData.painPoints.items} />
        </>
      ),
    },
    {
      id: "consulting-shift",
      content: (
        <>
          <SectionHead
            eyebrow="04 · Beratung neu gedacht"
            title={landingPageData.consultingShift.titleLine1}
            lede={landingPageData.consultingShift.description}
            blue
          />
          <WhyDbiCards
            cards={landingPageData.consultingShift.sections.map((section) => ({
              id: section.id,
              title: section.title,
              description: section.description,
              note: section.benefits.join(" · "),
            }))}
          />
        </>
      ),
    },
    {
      id: "technical-setup",
      content: (
        <>
          <SectionHead
            eyebrow="05 · Technisches Setup"
            title={landingPageData.technicalSetup.titleLine1}
            lede={landingPageData.technicalSetup.description}
            blue
          />
          <ScopeGrid
            items={landingPageData.technicalSetup.sections.map((section) => ({
              title: section.title,
              description: section.description,
            }))}
          />
        </>
      ),
    },
    {
      id: "compliance",
      content: (
        <>
          <SectionHead
            eyebrow="06 · Compliance"
            title={landingPageData.complianceMatters.titleLine1}
            lede={landingPageData.complianceMatters.description}
            blue
          />
          <WhyDbiCards
            cards={landingPageData.complianceMatters.sections.map((section) => ({
              id: section.id,
              title: section.title,
              description: section.description,
              note: section.benefits.join(" · "),
            }))}
          />
        </>
      ),
    },
    {
      id: "prozess",
      content: (
        <>
          <SectionHead eyebrow="07 · Vorgehensmodell" title="Zusammenarbeitsmodell" blue />
          <TimelineSteps
            steps={landingPageData.timeline.stages.map((item, index: number) => ({
              phase: `PHASE 0${index + 1}`,
              duration: item.duration,
              title: item.title,
              description: item.description,
            }))}
          />
        </>
      ),
    },
    {
      id: "live-prompting",
      content: (
        <>
          <SectionHead
            eyebrow="08 · Live Workflows"
            title={landingPageData.videoChaptersHeader.titleLine1}
            lede={landingPageData.videoChaptersHeader.description}
            blue
          />
          <ProcessSteps
            steps={landingPageData.livePromptingChapters.map((chapter, index) => ({
              number: index + 1,
              title: `${chapter.chapter} · ${chapter.timeSavings.hoursSaved}h -> ${chapter.timeSavings.minutesTaken}min`,
              description: chapter.tasks.join(" · "),
            }))}
          />
        </>
      ),
    },
    {
      id: "agent-dashboard",
      content: (
        <>
          <SectionHead
            eyebrow="09 · Agentic Workload"
            title={landingPageData.agentDashboard.titleLine1}
            lede={landingPageData.agentDashboard.description}
            blue
          />
          <AnswerCard paragraphs={[landingPageData.agentDashboard.introDescription]} />
          <CtaBlock
            title={landingPageData.agentDashboard.introTitle}
            description={landingPageData.agentDashboard.description}
            primaryCta={{
              label: landingPageData.agentDashboard.primaryButton.text,
              href: landingPageData.agentDashboard.primaryButton.href,
            }}
            secondaryCta={{
              label: landingPageData.agentDashboard.secondaryButton.text,
              href: landingPageData.agentDashboard.secondaryButton.href,
            }}
          />
        </>
      ),
    },
  ];

  return (
    <ServiceTemplatePage
      hero={hero}
      subnav={[
        { id: "einleitung", label: "Einführung" },
        { id: "leistungen", label: "Leistungsumfang" },
        { id: "pain-points", label: "Probleme" },
        { id: "consulting-shift", label: "Beratungsmodell" },
        { id: "technical-setup", label: "Technik" },
        { id: "compliance", label: "Compliance" },
        { id: "prozess", label: "Vorgehensmodell" },
        { id: "live-prompting", label: "Live Workflows" },
        { id: "agent-dashboard", label: "Agent Dashboard" },
        { id: "faq", label: "FAQ" },
      ]}
      sections={sections}
      faqEyebrow="10 · FAQ"
      faqTitle={landingPageData.faq.titleLine2}
      faqs={landingPageData.faq.faqs}
      relatedServices={landingPageData.relatedServices}
      ctaBlock={{
        title: landingPageData.cta.titleLine2,
        description: landingPageData.cta.description,
        primaryCta: {
          label: landingPageData.cta.primaryButton.text,
          href: landingPageData.cta.primaryButton.href,
        },
        secondaryCta: {
          label: landingPageData.cta.secondaryButton.text,
          href: landingPageData.cta.secondaryButton.href,
        },
      }}
    />
  );
}
